/**
 * Shared utilities for detecting static assets.
 * Used by logger, Sentry filters, and middleware to filter out noise from logs.
 */

/**
 * Common static files that should be filtered from logs.
 * These are noise and don't provide useful debugging information.
 */
export const STATIC_FILES = [
  '/favicon.ico',
  '/favicon-16x16.png',
  '/favicon-32x32.png',
  '/apple-touch-icon.png',
  '/android-chrome-192x192.png',
  '/android-chrome-512x512.png',
  '/emailferret_512-transparent.png',
  '/robots.txt',
  '/sitemap.xml',
  '/og-image.png',
] as const;

/**
 * Static file extensions that should be filtered from logs.
 */
export const STATIC_EXTENSIONS =
  /\.(svg|ico|png|jpg|jpeg|gif|webp|css|js|json|xml|txt|woff|woff2|ttf|eot|map)$/i;

/**
 * Check if a URL or path is a static asset.
 *
 * Filters out:
 * - Next.js static assets (`/_next/static/`)
 * - Common static files (favicon, robots.txt, etc.)
 * - Files with static file extensions (.js, .css, .svg, etc.)
 *
 * @param urlOrPath - URL or path to check (can be full URL, path, or undefined/null)
 * @returns true if the path is a static asset that should be filtered
 *
 * @example
 * ```typescript
 * isStaticAsset('/_next/static/chunks/main.js'); // true
 * isStaticAsset('/favicon.ico'); // true
 * isStaticAsset('/api/health'); // false
 * isStaticAsset('https://example.com/_next/static/chunk.js'); // true
 * ```
 */
export function isStaticAsset(urlOrPath: string | undefined | null): boolean {
  if (!urlOrPath || typeof urlOrPath !== 'string') {
    return false;
  }

  // Filter out Next.js static assets
  if (urlOrPath.includes('/_next/static/')) {
    return true;
  }

  // Filter out common static files
  if (STATIC_FILES.some((file) => urlOrPath === file || urlOrPath.endsWith(file))) {
    return true;
  }

  // Filter out other static file extensions
  if (STATIC_EXTENSIONS.test(urlOrPath)) {
    return true;
  }

  return false;
}
